import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUp, ArrowLeft, Brain } from "lucide-react";
import { EllieOrb } from "@/components/discovery/EllieOrb";
import { FeatureBubbles } from "@/components/companion/FeatureBubbles";
import { SuggestionCard } from "@/components/companion/SuggestionCard";
import { featureByKey } from "@/components/companion/features";
import { getEllieHistory, sendEllie, suggestionAction } from "@/services/unsaidService";

const DRAFT_KEY = "fio_ellie_draft";
const SCROLL_KEY = "fio_ellie_scroll";
const ss = { get: (k) => { try { return sessionStorage.getItem(k); } catch { return null; } }, set: (k, v) => { try { sessionStorage.setItem(k, v); } catch { /* ignore */ } }, del: (k) => { try { sessionStorage.removeItem(k); } catch { /* ignore */ } } };

export const EllieChat = ({ onBack }) => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [draft, setDraftState] = useState(() => ss.get(DRAFT_KEY) || "");
  const [thinking, setThinking] = useState(true);
  const [failed, setFailed] = useState(null); // null | { text }
  const [loaded, setLoaded] = useState(false);
  const scrollRef = useRef(null);
  const started = useRef(false);
  const restored = useRef(false);
  const setDraft = (v) => { setDraftState(v); ss.set(DRAFT_KEY, v); };

  const call = async (text) => {
    setFailed(null); setThinking(true);
    try {
      const res = await sendEllie(text);
      setMessages((prev) => [...prev.filter((m) => !m.pending), ...(res.messages || [])]);
    } catch { setFailed({ text }); }
    setThinking(false);
  };

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    getEllieHistory().then((h) => { setMessages(h); setLoaded(true); return call(null); })
      .catch(() => { setLoaded(true); setThinking(false); setFailed({ text: null }); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !loaded) return;
    const saved = !restored.current && ss.get(SCROLL_KEY);
    if (saved && messages.length) { restored.current = true; ss.del(SCROLL_KEY); el.scrollTop = Number(saved); return; }
    if (messages.length) restored.current = true;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [messages, thinking, failed, loaded]);

  const send = () => {
    const text = draft.trim();
    if (!text || thinking) return;
    setMessages((prev) => [...prev, { id: `local-${Date.now()}`, role: "user", text, pending: true }]);
    setDraft("");
    call(text);
  };

  const openFeature = (key, msg) => {
    const f = featureByKey(key);
    if (!f) return;
    ss.set(SCROLL_KEY, String(scrollRef.current?.scrollTop || 0));
    if (msg) suggestionAction(msg.id, "opened").catch(() => {});
    navigate(f.to);
  };
  const decline = (msg) => {
    setMessages((prev) => prev.map((m) => (m.id === msg.id ? { ...m, suggest: { ...m.suggest, status: "declined" } } : m)));
    suggestionAction(msg.id, "declined").catch(() => {});
  };
  const last = [...messages].reverse().find((m) => m.role === "ellie");
  const highlight = last?.suggest?.status === "open" ? last.suggest.area : null;

  return (
    <div data-testid="page-home-chat" className="fio-immersive flex h-[100dvh] flex-col"><div className="fio-ambient" aria-hidden><span /><i /></div>
      <div className="mx-auto w-full max-w-[600px] px-4 pt-4 sm:px-8">
        <div className="fio-appbar flex items-center justify-between rounded-full py-1.5 pl-4 pr-1.5" data-testid="app-header">
          <span className="flex items-center gap-3">
            <button data-testid="chat-back" onClick={onBack} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-4 w-4" /> Home
            </button>
            <button data-testid="chat-memory-link" onClick={() => navigate("/memory")} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" aria-label="What Ellie remembers">
              <Brain className="h-3.5 w-3.5" /> Memory
            </button>
          </span>
          <span className="flex items-center gap-2.5 pr-1">
            <span className="text-right">
              <span className="block font-heading text-sm font-semibold leading-tight text-foreground">Ellie</span>
              <span className="flex items-center justify-end gap-1 text-[10.5px] text-muted-foreground"><span className={`h-1.5 w-1.5 rounded-full ${thinking ? "bg-violet-500 fio-pulse-dot" : "bg-teal-500"}`} />{thinking ? "Reflecting…" : "Here with you"}</span>
            </span>
            <EllieOrb size={36} testId="chat-ellie-avatar" />
          </span>
        </div>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto no-scrollbar" data-testid="ellie-scroll">
        <div className="mx-auto flex w-full max-w-[600px] flex-col gap-4 px-5 py-6 sm:px-8">
          <AnimatePresence initial={false}>
            {messages.map((m) => (
              <motion.div key={m.id} data-testid="ellie-message" data-role={m.role}
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
                className={`flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`}>
                <div className={`max-w-[85%] whitespace-pre-wrap rounded-3xl px-5 py-3.5 text-base leading-relaxed ${m.role === "user" ? "rounded-br-lg bg-primary text-primary-foreground" : "rounded-bl-lg bg-card text-foreground ring-1 ring-border"}`}>
                  {m.text}
                </div>
                {m.role === "ellie" && m.suggest && <SuggestionCard suggest={m.suggest} onOpen={(k) => openFeature(k, m)} onDecline={() => decline(m)} />}
              </motion.div>
            ))}
          </AnimatePresence>
          {thinking && (
            <div className="flex justify-start" data-testid="ellie-thinking">
              <div className="flex items-center gap-1.5 rounded-3xl rounded-bl-lg bg-card px-5 py-4 ring-1 ring-border">
                {[0, 1, 2].map((i) => (<span key={i} className="h-2 w-2 animate-bounce rounded-full bg-primary/60" style={{ animationDelay: `${i * 0.15}s` }} />))}
              </div>
            </div>
          )}
          {failed && !thinking && (
            <div className="flex flex-col items-center gap-2 py-4 text-center" data-testid="ellie-retry">
              <p className="text-sm text-muted-foreground">The line went quiet. Let's reconnect.</p>
              <button data-testid="ellie-retry-btn" onClick={() => (loaded ? call(failed.text) : window.location.reload())} className="rounded-full border border-border bg-card px-4 py-2 text-sm font-medium hover:border-primary/40">Try again</button>
            </div>
          )}
        </div>
      </div>
      <div className="border-t border-border bg-background/95 backdrop-blur">
        <div className="mx-auto w-full max-w-[600px] px-5 pb-4 pt-3 sm:px-8">
          <FeatureBubbles highlight={highlight} onOpen={(k) => openFeature(k, last?.suggest?.area === k && highlight ? last : null)} />
          <div className="flex items-end gap-2 rounded-3xl bg-card p-2 ring-1 ring-border focus-within:ring-2 focus-within:ring-primary">
            <textarea data-testid="ellie-input" rows={1} value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Talk to Ellie…" aria-label="Message Ellie"
              className="max-h-40 flex-1 resize-none bg-transparent px-3 py-2.5 text-base text-foreground placeholder:text-muted-foreground/70 focus:outline-none" />
            <button data-testid="ellie-send" onClick={send} disabled={!draft.trim() || thinking}
              aria-label="Send" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-colors enabled:hover:bg-primary/90 disabled:opacity-40">
              <ArrowUp className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
